import * as React from "react"
import Tree from "react-d3-tree"
import { RawNodeDatum } from "react-d3-tree/lib/types/common"
import { useIntl } from "react-intl"

import TreeDisclaimer from "./TreeDisclaimer"
import TreeTitle from "./TreeTitle"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import SkeletonWrapper from "components/Shared/SkeletonWrapper"
import SwitchingTreeSkeleton from "components/CustomerBehavior/Switching/SwitchingTreeSkeleton/SwitchingTreeSkeleton"

import { useGetInformationTreeSwitching } from "components/CustomerBehavior/Switching/SwitchingBreakdown/hooks"
import {
	foreignObjectProps,
	nodeSize,
	SWITCHING_NODE,
	traslateConf,
} from "components/CustomerBehavior/Switching/SwitchingBreakdown/components/SwitchingTree/configurationTree"
import {
	generateAllNodes,
	generateTree,
} from "components/CustomerBehavior/Switching/utils"
import {
	InformationNodeGroup,
	InformationTitleFatherNode,
	ResponseInformationNode,
} from "components/CustomerBehavior/Switching/interfaces"
import GenerateNode from "components/Shared/GenerateNode"
import { RequestParameters } from "components/Shared/Filters/interfaces"

type SwitchingTreeProps = {
	switchingFilters: RequestParameters
	title?: InformationTitleFatherNode
}

const SwitchingTree: React.FC<SwitchingTreeProps> = (props) => {
	const { switchingFilters, title: fatherNodeTitle = {} } = props

	const select = (
		dataTreeSwitchingStub: ResponseInformationNode
	): RawNodeDatum =>
		generateTree(
			fatherNodeTitle?.description,
			fatherNodeTitle?.name,
			undefined,
			generateAllNodes({ ...dataTreeSwitchingStub })
		)

	const intl = useIntl()

	const {
		dataInformationTree,
		isPendingRequest,
		error,
		isError,
		isIdle,
		refetch,
	} = useGetInformationTreeSwitching(switchingFilters, select)

	if (isIdle) {
		return null
	}

	return (
		<AsyncMessageResponseHandler
			hasData={Boolean(
				dataInformationTree &&
					(dataInformationTree as InformationNodeGroup).isValidData
			)}
			error={error}
			isError={isError}
			isLoading={isPendingRequest}
			retry={refetch}
		>
			<SkeletonWrapper
				isLoading={isPendingRequest}
				skeleton={<SwitchingTreeSkeleton />}
			>
				<div data-testid="tree-component">
					<div className="container-graph-tree">
						<TreeTitle fatherNodeTitle={fatherNodeTitle} />
						<Tree
							translate={traslateConf}
							zoomable={false}
							initialDepth={1}
							data={dataInformationTree as RawNodeDatum}
							nodeSize={nodeSize}
							pathClassFunc={() => "custom-link"}
							pathFunc="step"
							separation={{ nonSiblings: 2, siblings: 1 }}
							renderCustomNodeElement={(rd3tProps) =>
								GenerateNode(
									{ ...rd3tProps, foreignObjectProps },
									SWITCHING_NODE,
									intl
								)
							}
							orientation="vertical"
						/>
					</div>
					<TreeDisclaimer />
				</div>
			</SkeletonWrapper>
		</AsyncMessageResponseHandler>
	)
}

export default SwitchingTree
